import type { UseItemsResult } from "../../hooks/use-items.ts";
import {
	getFullNameForSlug,
	getIconUrlForSlug,
	getShortNameForSlug,
} from "../dossier/loadout/loadout-slot-match.ts";
import { ItemIconHover } from "../item-icon-hover.tsx";

export function LoadoutSlot({
	label,
	equippedSlug,
	items,
	themeColorHex,
	invalid = false,
	hoverable = false,
	onClick,
}: {
	label: string;
	equippedSlug: string | null;
	items: UseItemsResult;
	themeColorHex: string | undefined;
	invalid?: boolean;
	hoverable?: boolean;
	onClick: () => void;
}) {
	const iconUrl = equippedSlug
		? getIconUrlForSlug(equippedSlug, items, "sm")
		: null;
	const hoverIconUrl =
		hoverable && equippedSlug
			? getIconUrlForSlug(equippedSlug, items, "lg")
			: null;
	const itemShortName = equippedSlug
		? getShortNameForSlug(equippedSlug, items)
		: null;
	const itemFullName = equippedSlug
		? getFullNameForSlug(equippedSlug, items)
		: null;

	const slotStyle: React.CSSProperties = {};
	if (equippedSlug && !invalid && themeColorHex) {
		slotStyle.borderBottomColor = themeColorHex;
		slotStyle.borderBottomWidth = 3;
	}

	const baseClasses =
		"group relative flex h-16 w-16 cursor-pointer flex-col items-center justify-center border transition-colors hover:bg-surface-container-low md:h-[88px] md:w-[88px]";
	const stateClasses = invalid
		? "border-error border-l-4 bg-error/5 hover:border-error"
		: "border-outline-variant bg-surface-container-lowest hover:border-outline";

	return (
		<button
			type="button"
			onClick={onClick}
			className={`${baseClasses} ${stateClasses}`}
			style={slotStyle}
			title={invalid ? "Not valid for assigned class" : undefined}
		>
			{iconUrl ? (
				<>
					<ItemIconHover
						iconUrl={iconUrl}
						hoverIconUrl={hoverIconUrl}
						name={itemFullName ?? itemShortName ?? equippedSlug ?? ""}
						themeColorHex={invalid ? undefined : themeColorHex}
						imgClassName="h-8 w-8 object-contain md:h-10 md:w-10"
						extraImgClassName={invalid ? "grayscale" : ""}
					/>
					{itemShortName && (
						<span
							title={itemFullName ?? undefined}
							className={`absolute inset-x-0 bottom-0.5 truncate px-0.5 text-center font-mono text-[8px] uppercase leading-tight md:bottom-1 md:text-[9px] ${invalid ? "text-error" : "text-outline"}`}
						>
							{itemShortName}
						</span>
					)}
					{invalid && (
						<span className="absolute top-0 right-0 border-error bg-error px-1 py-0.5 font-mono text-[7px] uppercase tracking-wide text-on-error leading-none">
							{"!"}
						</span>
					)}
				</>
			) : (
				<span className="font-mono text-[8px] text-outline uppercase tracking-wide md:text-[9px]">
					{label}
				</span>
			)}
		</button>
	);
}
